import React, { useState, useEffect } from 'react';
import {


  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Alert,
  ActivityIndicator,
  StyleSheet,
  Dimensions,
  KeyboardAvoidingView,
  Platform,
  StatusBar,
} from 'react-native';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
// Bulletproof icon imports with fallbacks
const FallbackIcon = ({ name, size = 24, color = '#666', style, ...props }) => (
  <View 
    style={[
      {
        width: size,
        height: size,
        backgroundColor: color,
        borderRadius: size / 2,
        opacity: 0.3
      },
      style
    ]}
    {...props}
  />
);

let Ionicons = FallbackIcon;
let FontAwesome5 = FallbackIcon;

try {
  const iconsModule = require('@expo/vector-icons');
  if (iconsModule) {
    if (iconsModule.Ionicons && typeof iconsModule.Ionicons === 'function') {
      Ionicons = iconsModule.Ionicons;
    }
    if (iconsModule.FontAwesome5 && typeof iconsModule.FontAwesome5 === 'function') {
      FontAwesome5 = iconsModule.FontAwesome5;
    }
  }
} catch (error) {
  console.warn('@expo/vector-icons not available, using fallback');
}

// Mock AsyncStorage for environments where it's not available
let AsyncStorage;
try {
  AsyncStorage = require('@react-native-async-storage/async-storage').default;
} catch (error) {
  console.warn('AsyncStorage not available, using mock implementation');
  AsyncStorage = {
    getItem: async (key) => null,
    setItem: async (key, value) => {},
    removeItem: async (key) => {}
  };
}

// Bulletproof service imports with fallbacks - NO DUPLICATE DECLARATIONS
class MockEducationService {
  async initialize() { return true; }
  async updateEducation(userId, educationData) { 
    return { success: true, message: 'Education updated successfully' }; 
  }
  async deleteEducation(userId, educationId) { 
    return { success: true, message: 'Education deleted successfully' }; 
  }
  validateEducationData(data) {
    const errors = {};
    if (!data.institution || data.institution.trim() === '') {
      errors.institution = 'Institution is required';
    }
    if (!data.degree || data.degree.trim() === '') {
      errors.degree = 'Degree is required';
    }
    return { isValid: Object.keys(errors).length === 0, errors };
  }
}

class MockUnifiedService {
  async initialize() { return true; }
  async trackUserActivity() { return { success: true }; }
}

class MockDashboardService {
  async initialize() { return true; }
  async updateDashboardMetrics() { return { success: true }; }
}

// Import services with fallbacks - SINGLE DECLARATION
let EducationService = MockEducationService;
let UnifiedService = MockUnifiedService;
let DashboardService = MockDashboardService;

try {
  const educationModule = require('../../services/EducationManagementService');
  if (educationModule && educationModule.default) {
    EducationService = educationModule.default;
  } else if (educationModule && typeof educationModule === 'function') {
    EducationService = educationModule;
  }
} catch (error) {
  console.warn('EducationManagementService not available, using mock');
}

try {
  const unifiedModule = require('../../services/UnifiedIntegrationService');
  if (unifiedModule && unifiedModule.default) {
    UnifiedService = unifiedModule.default;
  } else if (unifiedModule && typeof unifiedModule === 'function') {
    UnifiedService = unifiedModule;
  }
} catch (error) {
  console.warn('UnifiedIntegrationService not available, using mock');
}

try {
  const dashboardModule = require('../../services/DashboardIntegrationService');
  if (dashboardModule && dashboardModule.default) {
    DashboardService = dashboardModule.default;
  } else if (dashboardModule && typeof dashboardModule === 'function') {
    DashboardService = dashboardModule;
  }
} catch (error) {
  console.warn('DashboardIntegrationService not available, using mock');
}

const { width, height } = Dimensions.get('window');

const EditEducation = ({ navigation, route }) => {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // Get education data from navigation params
  const educationData = route.params?.education || {};
  const isEditing = !!educationData.id;

  // Services - USING RENAMED VARIABLES
  const [educationService] = useState(() => new EducationService());
  const [unifiedService] = useState(() => new UnifiedService());
  const [dashboardService] = useState(() => new DashboardService());

  // State management
  const [education, setEducation] = useState({
    id: educationData.id || null,
    institution: educationData.institution || '',
    degree: educationData.degree || '',
    fieldOfStudy: educationData.fieldOfStudy || '',
    startDate: educationData.startDate || '',
    endDate: educationData.endDate || '',
    grade: educationData.grade || '',
    description: educationData.description || '',
    isCurrentlyStudying: educationData.isCurrentlyStudying || false,
    activities: educationData.activities || '',
    location: educationData.location || '',
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Degree options
  const degreeOptions = [
    'High School Diploma',
    'Associate Degree',
    'Bachelor\'s Degree',
    'Master\'s Degree',
    'Doctoral Degree',
    'Professional Degree',
    'Certificate',
    'Diploma',
    'Other'
  ];

  useEffect(() => {
    initializeServices();
  }, []);

  useEffect(() => {
    // Check if there are changes compared to original data
    const hasEducationChanges = JSON.stringify(education) !== JSON.stringify({
      id: educationData.id || null,
      institution: educationData.institution || '',
      degree: educationData.degree || '',
      fieldOfStudy: educationData.fieldOfStudy || '',
      startDate: educationData.startDate || '',
      endDate: educationData.endDate || '',
      grade: educationData.grade || '',
      description: educationData.description || '',
      isCurrentlyStudying: educationData.isCurrentlyStudying || false,
      activities: educationData.activities || '',
      location: educationData.location || '',
    });
    setHasChanges(hasEducationChanges);
  }, [education, educationData]);

  const initializeServices = async () => {
    try {
      await Promise.all([
        educationService.initialize().catch(e => console.warn('Education service init failed:', e)),
        unifiedService.initialize().catch(e => console.warn('Unified service init failed:', e)),
        dashboardService.initialize().catch(e => console.warn('Dashboard service init failed:', e))
      ]);

      // Track screen view
      try {
        await unifiedService.trackUserActivity(isEditing ? 'edit_education_viewed' : 'add_education_viewed');
      } catch (error) {
        console.warn('Failed to track activity:', error);
      }
    } catch (error) {
      console.error('Error initializing services:', error);
    }
  };

  const validateInput = () => {
    const validation = educationService.validateEducationData(education);
    setErrors(validation.errors);
    return validation.isValid;
  };

  const handleSaveEducation = async () => {
    console.log('Save education initiated...');
    
    if (!validateInput()) {
      Alert.alert('Validation Error', 'Please correct the errors in the form.');
      return;
    }

    setSaving(true);
    
    try {
      const userId = 'demo_user'; // In real app, get from auth context
      const result = await educationService.updateEducation(userId, education);
      
      if (result.success) {
        // Save to cache
        const cacheKey = isEditing ? 'editedEducation' : 'newEducation';
        await AsyncStorage.setItem(cacheKey, JSON.stringify(education));
        
        // Track activity
        try {
          await unifiedService.trackUserActivity(isEditing ? 'education_updated' : 'education_added');
          await dashboardService.updateDashboardMetrics('education_updated', { userId });
        } catch (error) {
          console.warn('Failed to track activity:', error);
        }
        
        // Show success message and navigate back
        Alert.alert(
          'Success',
          result.message || `Education ${isEditing ? 'updated' : 'added'} successfully!`,
          [
            {
              text: 'OK',
              onPress: () => {
                if (navigation.canGoBack()) {
                  navigation.goBack();
                } else {
                  navigation.navigate('Profile');
                }
              }
            }
          ]
        );
      } else {
        throw new Error(result.error || 'Failed to save education');
      }
      
    } catch (error) {
      console.error('Error saving education:', error);
      Alert.alert('Error', error.message || 'Failed to save education. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEducation = async () => {
    if (!isEditing) return;

    Alert.alert(
      'Delete Education',
      'Are you sure you want to delete this education record?',
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Delete', 
          style: 'destructive',
          onPress: async () => {
            setSaving(true);
            try {
              const userId = 'demo_user';
              const result = await educationService.deleteEducation(userId, education.id);
              
              if (result.success) {
                // Track activity
                try {
                  await unifiedService.trackUserActivity('education_deleted');
                  await dashboardService.updateDashboardMetrics('education_deleted', { userId });
                } catch (error) {
                  console.warn('Failed to track activity:', error);
                }
                
                Alert.alert('Success', 'Education deleted successfully!', [
                  { text: 'OK', onPress: () => navigation.goBack() }
                ]);
              } else {
                throw new Error(result.error || 'Failed to delete education');
              }
            } catch (error) {
              console.error('Error deleting education:', error);
              Alert.alert('Error', 'Failed to delete education. Please try again.');
            } finally {
              setSaving(false);
            }
          }
        }
      ]
    );
  };

  const handleBackPress = () => {
    if (hasChanges) {
      Alert.alert(
        'Discard Changes',
        'Are you sure you want to discard your changes?',
        [
          { text: 'Keep Editing', style: 'cancel' },
          { 
            text: 'Discard', 
            style: 'destructive', 
            onPress: () => {
              if (navigation.canGoBack()) {
                navigation.goBack();
              } else {
                navigation.navigate('Profile');
              }
            }
          }
        ]
      );
    } else {
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else {
        navigation.navigate('Profile');
      }
    }
  };

  const updateEducation = (field, value) => {
    setEducation({ ...education, [field]: value });
  };

  return (
    <KeyboardAvoidingView 
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <StatusBar barStyle="dark-content" backgroundColor="#fff" />
      
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={handleBackPress}>
          <Ionicons name="arrow-back" size={24} color="#333" />
        </TouchableOpacity>
        
        <Text style={styles.headerTitle}>
          {isEditing ? 'Edit Education' : 'Add Education'}
        </Text>
        
        <View style={styles.headerActions}>
          {isEditing && (
            <TouchableOpacity 
              style={styles.deleteButton}
              onPress={handleDeleteEducation}
              disabled={saving}
            >
              <Ionicons name="trash-outline" size={20} color="#f44336" />
            </TouchableOpacity>
          )}
        </View>
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Institution */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Institution Details</Text>
          
          <View style={styles.inputContainer}>
            <Text style={styles.label}>Institution Name *</Text>
            <TextInput
              style={[styles.input, errors.institution && styles.inputError]}
              value={education.institution}
              onChangeText={(text) => updateEducation('institution', text)}
              placeholder="Enter institution name"
              autoCapitalize="words"
            />
            {errors.institution && <Text style={styles.errorText}>{errors.institution}</Text>}
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>Location</Text>
            <TextInput
              style={styles.input}
              value={education.location}
              onChangeText={(text) => updateEducation('location', text)}
              placeholder="City, State/Country"
              autoCapitalize="words"
            />
          </View>
        </View>

        {/* Degree Information */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Degree Information</Text>
          
          <View style={styles.inputContainer}>
            <Text style={styles.label}>Degree *</Text>
            <TextInput
              style={[styles.input, errors.degree && styles.inputError]}
              value={education.degree}
              onChangeText={(text) => updateEducation('degree', text)}
              placeholder="e.g., Bachelor's Degree"
              autoCapitalize="words"
            />
            {errors.degree && <Text style={styles.errorText}>{errors.degree}</Text>}
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>Field of Study</Text>
            <TextInput
              style={styles.input}
              value={education.fieldOfStudy}
              onChangeText={(text) => updateEducation('fieldOfStudy', text)}
              placeholder="e.g., Computer Science"
              autoCapitalize="words"
            />
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>Grade/GPA</Text>
            <TextInput
              style={styles.input}
              value={education.grade}
              onChangeText={(text) => updateEducation('grade', text)}
              placeholder="e.g., 3.8 GPA, First Class Honours"
            />
          </View>
        </View>

        {/* Duration */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Duration</Text>
          
          <View style={styles.dateRow}>
            <View style={styles.dateContainer}>
              <Text style={styles.label}>Start Date</Text>
              <TextInput
                style={styles.input}
                value={education.startDate}
                onChangeText={(text) => updateEducation('startDate', text)}
                placeholder="MM/YYYY"
              />
            </View>
            
            <View style={styles.dateContainer}>
              <Text style={styles.label}>End Date</Text>
              <TextInput
                style={[styles.input, education.isCurrentlyStudying && styles.inputDisabled]}
                value={education.isCurrentlyStudying ? 'Present' : education.endDate}
                onChangeText={(text) => updateEducation('endDate', text)}
                placeholder="MM/YYYY"
                editable={!education.isCurrentlyStudying}
              />
            </View>
          </View>

          <TouchableOpacity 
            style={styles.checkboxContainer}
            onPress={() => updateEducation('isCurrentlyStudying', !education.isCurrentlyStudying)}
          >
            <View style={[styles.checkbox, education.isCurrentlyStudying && styles.checkboxChecked]}>
              {education.isCurrentlyStudying && (
                <Ionicons name="checkmark" size={16} color="#fff" />
              )}
            </View>
            <Text style={styles.checkboxLabel}>I am currently studying here</Text>
          </TouchableOpacity>
        </View>

        {/* Additional Information */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Additional Information</Text>
          
          <View style={styles.inputContainer}>
            <Text style={styles.label}>Description</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={education.description}
              onChangeText={(text) => updateEducation('description', text)}
              placeholder="Describe your studies, achievements, relevant coursework..."
              multiline
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>Activities and Societies</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={education.activities}
              onChangeText={(text) => updateEducation('activities', text)}
              placeholder="Student organizations, clubs, sports teams..."
              multiline
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>
        </View>

        {/* Save Button */}
        <TouchableOpacity
          style={[styles.saveButton, (!hasChanges || saving) && styles.saveButtonDisabled]}
          onPress={handleSaveEducation}
          disabled={!hasChanges || saving}
        >
          {saving ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.saveButtonText}>
              {isEditing ? 'Update Education' : 'Add Education'}
            </Text>
          )}
        </TouchableOpacity>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    paddingTop: 50,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: '#f8f9fa',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  deleteButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: '#ffebee',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 20,
  },
  inputContainer: {
    marginBottom: 20,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#fff',
  },
  inputError: {
    borderColor: '#f44336',
    backgroundColor: '#ffebee',
  },
  inputDisabled: {
    backgroundColor: '#f5f5f5',
    color: '#999',
  },
  textArea: {
    minHeight: 100,
    textAlignVertical: 'top',
  },
  errorText: {
    color: '#f44336',
    fontSize: 14,
    marginTop: 4,
  },
  dateRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  dateContainer: {
    flex: 1,
    marginRight: 10,
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: '#ddd',
    borderRadius: 4,
    marginRight: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxChecked: {
    backgroundColor: '#4CAF50',
    borderColor: '#4CAF50',
  },
  checkboxLabel: {
    fontSize: 16,
    color: '#333',
  },
  saveButton: {
    backgroundColor: '#4CAF50',
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    marginTop: 20,
    marginBottom: 40,
  },
  saveButtonDisabled: {
    backgroundColor: '#ccc',
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
  },
});

export default EditEducation;
